import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { 
  Home, 
  Euro, 
  CheckCircle2,
  ArrowRight,
  TrendingUp,
  TrendingDown,
  Users,
  Calendar,
  Clock,
  Wrench
} from 'lucide-react';
import { Button } from '@/components/ui/button';

export default function RentabiliteAirbnbVsLocation() {
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <Helmet>
        <title>Airbnb vs Location Traditionnelle : Quelle Rentabilité en 2025 ?</title>
        <meta name="description" content="Comparez la rentabilité entre location Airbnb et location longue durée. Avantages, inconvénients, fiscalité et simulation pour choisir la meilleure stratégie." />
        <meta name="keywords" content="rentabilité airbnb, location airbnb vs longue durée, investissement airbnb rentable, LMNP airbnb" />
        <link rel="canonical" href="https://achat-immo-gagnant.fr/guides/rentabilite-airbnb-vs-location" />
      </Helmet>

      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
              <Home className="h-5 w-5 text-white" />
            </div>
            <span className="font-bold text-xl text-slate-800">Achat Immo Gagnant</span>
          </Link>
          <Link to="/">
            <Button className="bg-blue-600 hover:bg-blue-700 text-white">
              Comparer les deux stratégies
            </Button>
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="bg-gradient-to-r from-rose-600 to-pink-700 text-white py-16">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">
            Airbnb vs Location Traditionnelle
          </h1>
          <p className="text-xl text-rose-100 mb-8 max-w-2xl mx-auto">
            Quelle stratégie locative est la plus rentable pour votre bien ? 
            Analyse complète avec chiffres 2025.
          </p>
        </div>
      </section>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 py-12">
        
        {/* Intro */}
        <section className="mb-16">
          <div className="prose prose-lg text-slate-600">
            <p>
              Entre la <strong>location courte durée (Airbnb)</strong> et la <strong>location longue durée traditionnelle</strong>, le choix n'est pas toujours évident. Chaque stratégie a ses avantages et inconvénients. Voici un comparatif complet pour vous aider à décider.
            </p>
          </div>
        </section>

        {/* Comparison table */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-slate-800 mb-8 text-center">
            Le comparatif en un coup d'œil
          </h2>
          
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-slate-100">
                  <th className="p-4 text-left font-semibold text-slate-800 border-b-2 border-slate-300">Critère</th>
                  <th className="p-4 text-center font-semibold text-blue-700 border-b-2 border-blue-300">Location longue durée</th>
                  <th className="p-4 text-center font-semibold text-rose-700 border-b-2 border-rose-300">Location Airbnb</th>
                </tr>
              </thead>
              <tbody className="bg-white">
                <tr className="border-b border-slate-200">
                  <td className="p-4 font-medium text-slate-800">Rendement brut</td>
                  <td className="p-4 text-center text-blue-600">4-7%</td>
                  <td className="p-4 text-center text-rose-600 font-semibold">8-15%</td>
                </tr>
                <tr className="border-b border-slate-200 bg-slate-50">
                  <td className="p-4 font-medium text-slate-800">Gestion</td>
                  <td className="p-4 text-center text-blue-600 font-semibold">Passive</td>
                  <td className="p-4 text-center text-rose-600">Active (ménage, check-in...)</td>
                </tr>
                <tr className="border-b border-slate-200">
                  <td className="p-4 font-medium text-slate-800">Vacance locative</td>
                  <td className="p-4 text-center text-blue-600 font-semibold">Faible (~5%)</td>
                  <td className="p-4 text-center text-rose-600">Variable (30-40% hors saison)</td>
                </tr>
                <tr className="border-b border-slate-200 bg-slate-50">
                  <td className="p-4 font-medium text-slate-800">Usure du bien</td>
                  <td className="p-4 text-center text-blue-600 font-semibold">Normale</td>
                  <td className="p-4 text-center text-rose-600">Accélérée</td>
                </tr>
                <tr className="border-b border-slate-200">
                  <td className="p-4 font-medium text-slate-800">Réglementation</td>
                  <td className="p-4 text-center text-blue-600 font-semibold">Stable</td>
                  <td className="p-4 text-center text-rose-600">En durcissement</td>
                </tr>
                <tr className="border-b border-slate-200 bg-slate-50">
                  <td className="p-4 font-medium text-slate-800">Fiscalité (LMNP)</td>
                  <td className="p-4 text-center text-slate-600">Identique</td>
                  <td className="p-4 text-center text-slate-600">Identique</td>
                </tr>
                <tr>
                  <td className="p-4 font-medium text-slate-800">Investissement initial</td>
                  <td className="p-4 text-center text-blue-600 font-semibold">Moyen</td>
                  <td className="p-4 text-center text-rose-600">Élevé (ameublement, décoration)</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        {/* Avantages location traditionnelle */}
        <section className="mb-16">
          <div className="bg-blue-50 rounded-2xl p-8">
            <h2 className="text-2xl font-bold text-blue-800 mb-6 flex items-center gap-3">
              <Home className="h-8 w-8" />
              Location longue durée : les avantages
            </h2>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="flex items-start gap-3">
                <CheckCircle2 className="h-6 w-6 text-green-600 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-semibold text-slate-800">Revenus stables et prévisibles</h3>
                  <p className="text-sm text-slate-600">Le loyer tombe chaque mois, peu importe la saison.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle2 className="h-6 w-6 text-green-600 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-semibold text-slate-800">Gestion minimale</h3>
                  <p className="text-sm text-slate-600">Pas de ménage entre chaque locataire, pas de check-in/check-out.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle2 className="h-6 w-6 text-green-600 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-semibold text-slate-800">Pas de réglementation restrictive</h3>
                  <p className="text-sm text-slate-600">Aucune limite de jours, pas de numéro d'enregistrement.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle2 className="h-6 w-6 text-green-600 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-semibold text-slate-800">Usure normale du bien</h3>
                  <p className="text-sm text-slate-600">Un locataire longue durée prend généralement soin du logement.</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Avantages Airbnb */}
        <section className="mb-16">
          <div className="bg-rose-50 rounded-2xl p-8">
            <h2 className="text-2xl font-bold text-rose-800 mb-6 flex items-center gap-3">
              <TrendingUp className="h-8 w-8" />
              Location Airbnb : les avantages
            </h2>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="flex items-start gap-3">
                <CheckCircle2 className="h-6 w-6 text-green-600 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-semibold text-slate-800">Rendement potentiel 2x supérieur</h3>
                  <p className="text-sm text-slate-600">Les prix à la nuit permettent de dépasser largement les loyers traditionnels.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle2 className="h-6 w-6 text-green-600 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-semibold text-slate-800">Flexibilité d'usage</h3>
                  <p className="text-sm text-slate-600">Vous pouvez utiliser le bien vous-même entre deux locations.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle2 className="h-6 w-6 text-green-600 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-semibold text-slate-800">Pas de problème d'impayés</h3>
                  <p className="text-sm text-slate-600">Paiement encaissé avant l'arrivée du voyageur.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <CheckCircle2 className="h-6 w-6 text-green-600 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-semibold text-slate-800">Récupération facile du bien</h3>
                  <p className="text-sm text-slate-600">Pas de procédure d'expulsion : le voyageur part à la date prévue.</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Exemple chiffré */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-slate-800 mb-8">
            Exemple chiffré : T2 de 50m² à Bordeaux
          </h2>
          
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl p-6 border-2 border-blue-300">
              <h3 className="text-xl font-bold text-blue-700 mb-4">Location longue durée</h3>
              <ul className="space-y-3 text-slate-600">
                <li className="flex justify-between">
                  <span>Loyer mensuel</span>
                  <span className="font-semibold">750€</span>
                </li>
                <li className="flex justify-between">
                  <span>Revenus annuels</span>
                  <span className="font-semibold">9 000€</span>
                </li>
                <li className="flex justify-between">
                  <span>Charges (20%)</span>
                  <span className="text-red-600">-1 800€</span>
                </li>
                <li className="flex justify-between border-t border-slate-200 pt-3 mt-3">
                  <span className="font-semibold">Revenu net</span>
                  <span className="font-bold text-blue-700">7 200€/an</span>
                </li>
              </ul>
              <div className="mt-4 p-3 bg-blue-50 rounded-lg text-center">
                <span className="text-sm text-slate-600">Rendement net sur 150 000€</span>
                <div className="text-2xl font-bold text-blue-700">4,8%</div>
              </div>
            </div>
            
            <div className="bg-white rounded-xl p-6 border-2 border-rose-300">
              <h3 className="text-xl font-bold text-rose-700 mb-4">Location Airbnb</h3>
              <ul className="space-y-3 text-slate-600">
                <li className="flex justify-between">
                  <span>Prix/nuit moyen</span>
                  <span className="font-semibold">85€</span>
                </li>
                <li className="flex justify-between">
                  <span>Nuits louées (65%)</span>
                  <span className="font-semibold">237 nuits</span>
                </li>
                <li className="flex justify-between">
                  <span>CA brut</span>
                  <span className="font-semibold">20 145€</span>
                </li>
                <li className="flex justify-between">
                  <span>Charges (35%)</span>
                  <span className="text-red-600">-7 051€</span>
                </li>
                <li className="flex justify-between border-t border-slate-200 pt-3 mt-3">
                  <span className="font-semibold">Revenu net</span>
                  <span className="font-bold text-rose-700">13 094€/an</span>
                </li>
              </ul>
              <div className="mt-4 p-3 bg-rose-50 rounded-lg text-center">
                <span className="text-sm text-slate-600">Rendement net sur 150 000€</span>
                <div className="text-2xl font-bold text-rose-700">8,7%</div>
              </div>
            </div>
          </div>
          
          <div className="mt-6 bg-amber-50 rounded-xl p-4 border border-amber-200">
            <p className="text-sm text-amber-800">
              <strong>Note :</strong> Les charges Airbnb incluent le ménage, les consommables, la commission plateforme (3%), et une provision pour renouvellement du mobilier. Le taux d'occupation de 65% est une moyenne réaliste.
            </p>
          </div>
        </section>

        {/* Quand choisir quoi */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-slate-800 mb-8">
            Quelle stratégie choisir ?
          </h2>
          
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-blue-50 rounded-xl p-6">
              <h3 className="text-xl font-bold text-blue-800 mb-4">Choisissez la location longue durée si :</h3>
              <ul className="space-y-3">
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700">Vous recherchez un investissement <strong>passif</strong></span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700">Vous habitez loin du bien</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700">Le bien n'est pas dans une zone touristique</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700">Vous préférez la <strong>sécurité</strong> au rendement maximum</span>
                </li>
              </ul>
            </div>
            
            <div className="bg-rose-50 rounded-xl p-6">
              <h3 className="text-xl font-bold text-rose-800 mb-4">Choisissez Airbnb si :</h3>
              <ul className="space-y-3">
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="h-5 w-5 text-rose-600 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700">Vous pouvez gérer activement (ou déléguer)</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="h-5 w-5 text-rose-600 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700">Le bien est en <strong>zone touristique</strong></span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="h-5 w-5 text-rose-600 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700">Vous voulez utiliser le bien occasionnellement</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="h-5 w-5 text-rose-600 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-700">Vous visez le <strong>rendement maximum</strong></span>
                </li>
              </ul>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="bg-gradient-to-r from-rose-600 to-pink-700 rounded-2xl p-8 text-center text-white">
          <h2 className="text-3xl font-bold mb-4">
            Comparez les deux stratégies pour votre bien
          </h2>
          <p className="text-rose-100 mb-6 max-w-xl mx-auto">
            Notre simulateur calcule automatiquement la rentabilité en location longue durée ET en Airbnb.
          </p>
          <Link to="/">
            <Button size="lg" className="bg-white text-rose-600 hover:bg-rose-50 text-lg px-8">
              Simuler mon investissement
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </section>

        {/* Related */}
        <section className="mt-16">
          <h2 className="text-2xl font-bold text-slate-800 mb-6">Guides connexes</h2>
          <div className="grid md:grid-cols-2 gap-4">
            <Link to="/guides/simulateur-rentabilite-locative" className="bg-white rounded-lg p-4 border border-slate-200 hover:border-blue-300 transition-colors">
              <h3 className="font-semibold text-blue-600">Simulateur rentabilité locative</h3>
              <p className="text-sm text-slate-600">Le guide complet pour calculer votre rentabilité</p>
            </Link>
            <Link to="/guides/fiscalite-location-meublee" className="bg-white rounded-lg p-4 border border-slate-200 hover:border-blue-300 transition-colors">
              <h3 className="font-semibold text-blue-600">Fiscalité LMNP</h3>
              <p className="text-sm text-slate-600">Optimisez vos impôts en location meublée</p>
            </Link>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-12 mt-16">
        <div className="max-w-6xl mx-auto px-4 text-center text-slate-500 text-sm">
          © 2025 Achat Immo Gagnant. Tous droits réservés.
        </div>
      </footer>
    </div>
  );
}
